import type { MetricRangePreset, MetricRangeValue } from "./types";

/** Offset UTC legible para la UI, ej. "UTC-4" */
export function getUtcOffsetLabel(date: Date = new Date()): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return minutes === 0
    ? `UTC${sign}${hours}`
    : `UTC${sign}${hours}:${String(minutes).padStart(2, "0")}`;
}

/** Floor a inicio de hora UTC en ms (docs Firestore siguen siendo por hora UTC). */
function utcHourFloorMs(date: Date): number {
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    0,
    0,
    0
  );
}

/** Inicio del día civil local (00:00:00.000 local → epoch ms). */
export function localDayStartMs(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
}

/** Inicio del día civil local siguiente. */
export function localDayEndMs(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1,
    0,
    0,
    0,
    0
  ).getTime();
}

/** YYYY-MM-DD del calendario local. */
export function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** YYYY-MM del calendario local. */
export function formatLocalYearMonth(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Doc id de la hora UTC actual: YYYY-MM-DDTHH (coincide con docs del cron). */
export function getCurrentUtcHourDocId(now: Date = new Date()): string {
  return formatUtcHourDocId(now);
}

/** Construye docId desde partes UTC */
export function formatUtcHourDocId(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const h = String(date.getUTCHours()).padStart(2, "0");
  return `${y}-${m}-${d}T${h}`;
}

/** @deprecated Prefer formatLocalDate for UI ranges. Kept for callers/tests. */
export function formatUtcDate(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** @deprecated Prefer formatLocalYearMonth for UI ranges. */
export function formatUtcYearMonth(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Resuelve un preset a valores de query.
 * "Hoy" / mes / día custom = calendario **local** del navegador.
 * startMs/endMs son epoch (absolutos); los docs siguen siendo horas UTC en Firestore.
 */
export function resolveMetricRange(
  preset: MetricRangePreset,
  now: Date = new Date(),
  custom?: { startMs?: number; endMs?: number; date?: string }
): MetricRangeValue {
  const nowMs = now.getTime();
  const hourEndMs = utcHourFloorMs(now) + 60 * 60 * 1000;

  switch (preset) {
    case "today": {
      const start = localDayStartMs(now);
      return {
        preset,
        date: formatLocalDate(now),
        startMs: start,
        endMs: nowMs + 1,
      };
    }
    case "last24h": {
      return {
        preset,
        startMs: hourEndMs - 24 * 60 * 60 * 1000,
        endMs: hourEndMs,
      };
    }
    case "last7d": {
      return {
        preset,
        startMs: hourEndMs - 7 * 24 * 60 * 60 * 1000,
        endMs: hourEndMs,
      };
    }
    case "month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime();
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0).getTime();
      return {
        preset,
        yearMonth: formatLocalYearMonth(now),
        startMs: start,
        endMs: Math.min(end, nowMs + 1),
      };
    }
    case "custom": {
      if (custom?.date) {
        const [y, m, d] = custom.date.split("-").map(Number);
        const local = new Date(y, m - 1, d, 12, 0, 0, 0);
        const start = localDayStartMs(local);
        const end = localDayEndMs(local);
        return {
          preset,
          date: custom.date,
          startMs: start,
          endMs: end,
        };
      }
      const startMs = custom?.startMs ?? nowMs - 24 * 60 * 60 * 1000;
      const endMs = custom?.endMs ?? nowMs;
      return { preset, startMs, endMs };
    }
    default:
      return resolveMetricRange("today", now);
  }
}

/** Horas esperadas en el rango [startMs, endMs) */
export function expectedHoursInRange(startMs: number, endMs: number): number {
  if (endMs <= startMs) return 0;
  return Math.ceil((endMs - startMs) / (60 * 60 * 1000));
}
